import {
  AXIS_COLOR_ON_DARK,
  AXIS_COLOR_ON_LIGHT,
  BASE_RULER_FONT_SIZE,
  DARK_TRAINING_DOT_COLORS,
  DEFAULT_COLOR_ON_DARK,
  DEFAULT_COLOR_ON_LIGHT,
  getScaledLabelFont,
  GRID_COLOR_ON_DARK,
  GRID_COLOR_ON_LIGHT,
  LIGHT_TRAINING_DOT_COLORS,
  PITCH_REPORT_FONT_SIZE,
  SVG_FALLBACK_CROSSHAIR_COLOR,
} from 'enums/canvas';
import { CROSS2_SVG, CROSS_SVG, CROSSHAIR_OUTLINE_SVG } from 'enums/svg-xmls';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { IN_TO_CM, RAD_FULL_ROTATION } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { IBreakConfig } from 'lib_ts/interfaces/i-break-config';
import { ITrajektRefBreak } from 'lib_ts/interfaces/pitches';

interface IConfig {
  isDark: boolean;
  inchToPx: number;
  maxIn: number; // controls the actual bounds of the canvas
  maxAxisIn: number; // controls where the lines and tick marks stop
  widthScale: number;
  step?: number;
}

export class BreakCanvas {
  // fallback to empty breaks
  static getMedian(breaks: ITrajektRefBreak[]): ITrajektRefBreak {
    if (breaks.length === 0) {
      return {
        xInches: 0,
        zInches: 0,
      };
    }

    return MiscHelper.getMedianObject(breaks) as ITrajektRefBreak;
  }

  static makeSquare(isDark: boolean) {
    return new BreakCanvas({
      isDark: isDark,
      inchToPx: 13,
      maxIn: 30,
      maxAxisIn: 22,
      widthScale: 2,
    });
  }

  static makeBigSquare(isDark: boolean) {
    return new BreakCanvas({
      isDark: isDark,
      inchToPx: 20,
      maxIn: 30,
      maxAxisIn: 30,
      widthScale: 3,
    });
  }

  readonly CONFIG: IBreakConfig;

  private readonly CONSTANTS_PX: {
    origin_x: number;
    origin_y: number;
  };

  constructor(config: IConfig) {
    this.CONFIG = {
      isDark: config.isDark,
      colors: {
        default: config.isDark ? DEFAULT_COLOR_ON_DARK : DEFAULT_COLOR_ON_LIGHT,
        axes: config.isDark ? AXIS_COLOR_ON_DARK : AXIS_COLOR_ON_LIGHT,
        grid: config.isDark ? GRID_COLOR_ON_DARK : GRID_COLOR_ON_LIGHT,
      },
      canvas: {
        inchToPx: config.inchToPx,
        width_px: 2 * config.maxIn * config.inchToPx,
        height_px: 2 * config.maxIn * config.inchToPx,
        widthScale: config.widthScale,
        aspectRatio: '1', // assuming we never draw breaks w/ diff width vs height limits
      },
      x: {
        min_in: -config.maxIn,
        max_in: config.maxIn,

        min_axis_in: -config.maxAxisIn,
        max_axis_in: config.maxAxisIn,

        step: config.step ?? 0.00001,
      },
      y: {
        min_in: -config.maxIn,
        max_in: config.maxIn,

        min_axis_in: -config.maxAxisIn,
        max_axis_in: config.maxAxisIn,

        step: config.step ?? 0.00001,
      },
    };

    /** must be after plate is defined */
    this.CONSTANTS_PX = {
      origin_x: this.inchToPxX(0),
      origin_y: this.inchToPxY(0),
    };
  }

  private inchToPxY(yInches: number) {
    return (this.CONFIG.y.max_in - yInches) * this.CONFIG.canvas.inchToPx;
  }

  private inchToPxX(xInches: number) {
    return (xInches - this.CONFIG.x.min_in) * this.CONFIG.canvas.inchToPx;
  }

  private getScaledWidth(relValue: number) {
    return relValue * this.CONFIG.canvas.widthScale;
  }

  drawRulersForEditBreaks(
    ctx: CanvasRenderingContext2D,
    tickIntervalIn?: number
  ) {
    const x_ruler_pos_y = Math.min(
      this.CONSTANTS_PX.origin_y,
      this.inchToPxY(this.CONFIG.y.min_axis_in)
    );

    const y_ruler_pos_x = Math.min(
      this.CONSTANTS_PX.origin_x,
      this.inchToPxY(this.CONFIG.x.min_axis_in)
    );

    const DRAW_AXES = true;

    if (DRAW_AXES) {
      ctx.lineWidth = this.getScaledWidth(2);
      ctx.strokeStyle = this.CONFIG.colors.grid;

      // hor axis
      ctx.beginPath();
      ctx.moveTo(this.inchToPxY(this.CONFIG.x.min_axis_in), x_ruler_pos_y);
      ctx.lineTo(this.inchToPxY(this.CONFIG.x.max_axis_in), x_ruler_pos_y);
      ctx.closePath();
      ctx.stroke();

      // vert axis
      ctx.beginPath();
      ctx.moveTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.min_axis_in));
      ctx.lineTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.max_axis_in));
      ctx.closePath();
      ctx.stroke();
    }

    const DRAW_LABELS = true;

    if (DRAW_LABELS) {
      const safeTickIntervalIn = tickIntervalIn ?? 5;

      ctx.font = getScaledLabelFont(
        this.CONFIG.canvas.widthScale,
        BASE_RULER_FONT_SIZE
      );
      ctx.fillStyle = this.CONFIG.colors.default;

      const minX = Math.ceil(this.CONFIG.x.min_axis_in);
      const maxX = Math.floor(this.CONFIG.x.max_axis_in);

      /** draw x ruler tick marks + units per foot */
      ArrayHelper.getIntegerOptions(minX, maxX).forEach((o) => {
        const intValue = parseInt(o.value);

        if (intValue % safeTickIntervalIn !== 0) {
          return;
        }

        const x = this.inchToPxX(intValue);

        ctx.beginPath();
        ctx.moveTo(x, x_ruler_pos_y);
        ctx.lineTo(x, x_ruler_pos_y - this.getScaledWidth(5));
        ctx.closePath();
        ctx.stroke();

        if (intValue !== 0) {
          ctx.fillText(
            `${o.value}"`,
            x - this.getScaledWidth(intValue > 0 ? 6 : 9),
            x_ruler_pos_y - this.getScaledWidth(10)
          );
        }
      });

      /** draw y ruler down the middle */

      const minY = Math.ceil(this.CONFIG.y.min_axis_in);
      const maxY = Math.floor(this.CONFIG.y.max_axis_in);

      /** draw y ruler tick marks + units per foot */
      ArrayHelper.getIntegerOptions(minY, maxY).forEach((o) => {
        const intValue = parseInt(o.value);

        if (intValue % safeTickIntervalIn !== 0) {
          return;
        }

        const y = this.inchToPxY(intValue);

        ctx.beginPath();
        ctx.moveTo(y_ruler_pos_x, y);
        ctx.lineTo(y_ruler_pos_x + this.getScaledWidth(5), y);
        ctx.closePath();
        ctx.stroke();

        if (intValue !== 0) {
          ctx.fillText(
            `${intValue}"`,
            y_ruler_pos_x + this.getScaledWidth(8),
            y + this.getScaledWidth(4)
          );
        }
      });
    }
  }

  // used for PP report and PT training
  drawRulersInches(ctx: CanvasRenderingContext2D, tickInterval: number) {
    const x_ruler_pos_y = Math.min(
      this.CONSTANTS_PX.origin_y,
      this.inchToPxY(this.CONFIG.y.min_axis_in)
    );

    const y_ruler_pos_x = Math.min(
      this.CONSTANTS_PX.origin_x,
      this.inchToPxY(this.CONFIG.x.min_axis_in)
    );

    const DRAW_AXES = true;

    if (DRAW_AXES) {
      ctx.lineWidth = this.getScaledWidth(2);
      ctx.strokeStyle = this.CONFIG.colors.axes;

      // hor axis
      ctx.beginPath();
      ctx.moveTo(this.inchToPxY(this.CONFIG.x.min_axis_in), x_ruler_pos_y);
      ctx.lineTo(this.inchToPxY(this.CONFIG.x.max_axis_in), x_ruler_pos_y);
      ctx.closePath();
      ctx.stroke();

      // vert axis
      ctx.beginPath();
      ctx.moveTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.min_axis_in));
      ctx.lineTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.max_axis_in));
      ctx.closePath();
      ctx.stroke();
    }

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      BASE_RULER_FONT_SIZE
    );

    ctx.fillStyle = this.CONFIG.colors.axes;

    const minX = Math.ceil(this.CONFIG.x.min_axis_in);
    const maxX = Math.floor(this.CONFIG.x.max_axis_in);

    const unit = '"';

    /** draw x min and max units only (below ruler line) */
    // ? value is in cm for metric, inches otherwise
    for (let value = minX; value <= maxX; value++) {
      if (value === 0) {
        // skip origin
        continue;
      }

      if (value % tickInterval !== 0) {
        // skip all the numbers between intervals
        continue;
      }

      // convert to inches (if necessary)
      const x = this.inchToPxX(value);
      const label = `${value}${unit}`;

      if (value < 0) {
        ctx.fillText(
          label,
          x + this.getScaledWidth(4),
          x_ruler_pos_y + this.getScaledWidth(16)
        );
        continue;
      }

      ctx.fillText(
        label,
        x - this.getScaledWidth(28),
        x_ruler_pos_y + this.getScaledWidth(16)
      );
    }

    const minY = Math.ceil(this.CONFIG.y.min_axis_in);
    const maxY = Math.floor(this.CONFIG.y.max_axis_in);

    /** draw y min and max units only, without tick marks */
    // ? value is in cm for metric, inches otherwise
    for (let value = minY; value <= maxY; value++) {
      if (value === 0) {
        // skip origin
        continue;
      }

      if (value % tickInterval !== 0) {
        // skip all the numbers between intervals
        continue;
      }

      const y = this.inchToPxY(value);
      const label = `${value}${unit}`;

      if (value < 0) {
        ctx.fillText(
          label,
          y_ruler_pos_x - this.getScaledWidth(32),
          y - this.getScaledWidth(6)
        );
        continue;
      }

      ctx.fillText(
        label,
        y_ruler_pos_x - this.getScaledWidth(28),
        y + this.getScaledWidth(16)
      );
    }
  }

  drawPitchReportRulersInches(ctx: CanvasRenderingContext2D) {
    const x_ruler_pos_y = Math.min(
      this.CONSTANTS_PX.origin_y,
      this.inchToPxY(this.CONFIG.y.min_axis_in)
    );

    const y_ruler_pos_x = Math.min(
      this.CONSTANTS_PX.origin_x,
      this.inchToPxY(this.CONFIG.x.min_axis_in)
    );

    const DRAW_AXES = true;

    if (DRAW_AXES) {
      ctx.lineWidth = this.getScaledWidth(2);
      ctx.strokeStyle = this.CONFIG.colors.axes;

      // hor axis
      ctx.beginPath();
      ctx.moveTo(this.inchToPxY(this.CONFIG.x.min_axis_in), x_ruler_pos_y);
      ctx.lineTo(this.inchToPxY(this.CONFIG.x.max_axis_in), x_ruler_pos_y);
      ctx.closePath();
      ctx.stroke();

      // vert axis
      ctx.beginPath();
      ctx.moveTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.min_axis_in));
      ctx.lineTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.max_axis_in));
      ctx.closePath();
      ctx.stroke();
    }

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      PITCH_REPORT_FONT_SIZE
    );

    ctx.fillStyle = this.CONFIG.colors.axes;

    const xLabels = [-30, 30];

    xLabels.forEach((value) => {
      // convert to inches (if necessary)
      const x = this.inchToPxX(value);
      const label = `${value}"`;

      if (value < 0) {
        ctx.fillText(
          label,
          x + this.getScaledWidth(4),
          x_ruler_pos_y + this.getScaledWidth(16)
        );
      } else {
        ctx.fillText(
          label,
          x - this.getScaledWidth(22),
          x_ruler_pos_y + this.getScaledWidth(16)
        );
      }
    });

    const yLabels = [-30, 30];

    yLabels.forEach((value) => {
      const y = this.inchToPxY(value);
      const label = `${value}"`;

      if (value < 0) {
        ctx.fillText(
          label,
          y_ruler_pos_x - this.getScaledWidth(26),
          y - this.getScaledWidth(6)
        );
      } else {
        ctx.fillText(
          label,
          y_ruler_pos_x - this.getScaledWidth(22),
          y + this.getScaledWidth(16)
        );
      }
    });
  }

  // only used for PT training
  drawRulersCm(ctx: CanvasRenderingContext2D, tickInterval: number) {
    const x_ruler_pos_y = Math.min(
      this.CONSTANTS_PX.origin_y,
      this.inchToPxY(this.CONFIG.y.min_axis_in)
    );

    const y_ruler_pos_x = Math.min(
      this.CONSTANTS_PX.origin_x,
      this.inchToPxY(this.CONFIG.x.min_axis_in)
    );

    const DRAW_AXES = true;

    if (DRAW_AXES) {
      ctx.lineWidth = this.getScaledWidth(2);
      ctx.strokeStyle = this.CONFIG.colors.axes;

      // hor axis
      ctx.beginPath();
      ctx.moveTo(this.inchToPxY(this.CONFIG.x.min_axis_in), x_ruler_pos_y);
      ctx.lineTo(this.inchToPxY(this.CONFIG.x.max_axis_in), x_ruler_pos_y);
      ctx.closePath();
      ctx.stroke();

      // vert axis
      ctx.beginPath();
      ctx.moveTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.min_axis_in));
      ctx.lineTo(y_ruler_pos_x, this.inchToPxY(this.CONFIG.y.max_axis_in));
      ctx.closePath();
      ctx.stroke();
    }

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      BASE_RULER_FONT_SIZE
    );

    ctx.fillStyle = this.CONFIG.colors.axes;

    const minX = Math.ceil(IN_TO_CM * this.CONFIG.x.min_axis_in);
    const maxX = Math.floor(IN_TO_CM * this.CONFIG.x.max_axis_in);

    const unit = 'cm';

    /** draw x min and max units only (below ruler line) */
    // ? value is in cm for metric, inches otherwise
    for (let value = minX; value <= maxX; value++) {
      if (value === 0) {
        // skip origin
        continue;
      }

      if (value % tickInterval !== 0) {
        // skip all the numbers between intervals
        continue;
      }

      // convert to inches (if necessary)
      const x = this.inchToPxX(value / IN_TO_CM);
      const label = `${value}${unit}`;

      if (value < 0) {
        ctx.fillText(
          label,
          x + this.getScaledWidth(0),
          x_ruler_pos_y + this.getScaledWidth(16)
        );
        continue;
      }

      ctx.fillText(
        label,
        x - this.getScaledWidth(40),
        x_ruler_pos_y + this.getScaledWidth(16)
      );
    }

    const minY = Math.ceil(IN_TO_CM * this.CONFIG.y.min_axis_in);
    const maxY = Math.floor(IN_TO_CM * this.CONFIG.y.max_axis_in);

    /** draw y min and max units only, without tick marks */
    // ? value is in cm for metric, inches otherwise
    for (let value = minY; value <= maxY; value++) {
      if (value === 0) {
        // skip origin
        continue;
      }

      if (value % tickInterval !== 0) {
        // skip all the numbers between intervals
        continue;
      }

      const y = this.inchToPxY(value / IN_TO_CM);
      const label = `${value}${unit}`;

      if (value < 0) {
        ctx.fillText(
          label,
          y_ruler_pos_x - this.getScaledWidth(32),
          y - this.getScaledWidth(6)
        );
        continue;
      }

      ctx.fillText(
        label,
        y_ruler_pos_x - this.getScaledWidth(28),
        y + this.getScaledWidth(16)
      );
    }
  }

  drawGrid(
    ctx: CanvasRenderingContext2D,
    tickIntervalIn?: number,
    // minimal - skip drawing outside lines
    minimal?: boolean
  ) {
    const safeTickIntervalIn = tickIntervalIn ?? 5;

    ctx.lineWidth = this.getScaledWidth(1);
    ctx.strokeStyle = this.CONFIG.colors.grid;

    const minX = this.inchToPxX(this.CONFIG.x.min_axis_in);
    const maxX = this.inchToPxX(this.CONFIG.x.max_axis_in);
    // y is flipped
    const minY = this.inchToPxY(this.CONFIG.y.max_axis_in);
    const maxY = this.inchToPxY(this.CONFIG.y.min_axis_in);

    const xIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil(this.CONFIG.x.min_axis_in),
      Math.floor(this.CONFIG.x.max_axis_in)
    );

    /** draw x lines per interval */
    xIntegerOptions.forEach((o, i) => {
      if (minimal && (i === 0 || i === xIntegerOptions.length - 1)) {
        // don't draw first and last lines
        return;
      }

      const intValue = parseInt(o.value);

      if (intValue % safeTickIntervalIn !== 0) {
        return;
      }

      const x = this.inchToPxX(intValue);

      ctx.beginPath();
      ctx.moveTo(x, minY);
      ctx.lineTo(x, maxY);
      ctx.closePath();
      ctx.stroke();
    });

    const yIntegerOptions = ArrayHelper.getIntegerOptions(
      Math.ceil(this.CONFIG.y.min_axis_in),
      Math.floor(this.CONFIG.y.max_axis_in)
    );

    /** draw y lines per interval */
    yIntegerOptions.forEach((o, i) => {
      if (minimal && (i === 0 || i === yIntegerOptions.length - 1)) {
        // don't draw first and last lines
        return;
      }

      const intValue = parseInt(o.value);

      if (intValue % safeTickIntervalIn !== 0) {
        return;
      }

      const y = this.inchToPxY(intValue);

      ctx.beginPath();
      ctx.moveTo(minX, y);
      ctx.lineTo(maxX, y);
      ctx.closePath();
      ctx.stroke();
    });
  }

  drawCheckCircled(
    ctx: CanvasRenderingContext2D,
    config: {
      mode: 'filled' | 'outlined';
      value: ITrajektRefBreak;
      width_px: number;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(config.width_px);

      const img = new Image(scaledSize, scaledSize);

      img.src =
        config.mode === 'filled'
          ? '/img/check-circled-filled.svg'
          : '/img/check-circled-outlined.svg';

      img.onload = () => {
        const x = this.inchToPxX(config.value.xInches);
        const y = this.inchToPxY(config.value.zInches);
        const half = scaledSize / 2;

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawCrosshairs(
    ctx: CanvasRenderingContext2D,
    config: {
      as: 'cross' | 'crosshairs';
      value: ITrajektRefBreak;
      width_px: number;
      color?: string;
      beforeDraw?: () => void;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(config.width_px);

      const img = new Image(scaledSize, scaledSize);

      const svgXml = (config.as === 'cross' ? CROSS_SVG : CROSSHAIR_OUTLINE_SVG)
        .replaceAll(
          'currentColor',
          // default is to replace the white with red
          config.color ?? SVG_FALLBACK_CROSSHAIR_COLOR
        )
        .replaceAll('outlineColor', this.CONFIG.isDark ? '#1F2123' : '#FFFFFF');

      img.onload = () => {
        const x = this.inchToPxX(config.value.xInches);
        const y = this.inchToPxY(config.value.zInches);
        const half = scaledSize / 2;

        // img.onload is async, so we may want to do something right before drawing (e.g. clear canvas)
        if (config.beforeDraw) {
          config.beforeDraw();
        }

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };

      // ? encodeURIComponent is necessary to safely handle special chars, e.g. in hex-style color codes
      img.src =
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgXml);
    });
  }

  drawTargetBubble(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      width_px: number;
      colorHex?: string;
      beforeDraw?: () => void;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(config.width_px);

      const img = new Image(scaledSize, scaledSize);

      img.src = `/img/target-bubble.svg`;

      img.onload = () => {
        const x = this.inchToPxX(config.value.xInches);
        const y = this.inchToPxY(config.value.zInches);
        const half = scaledSize / 2;

        // img.onload is async, so we may want to do something right before drawing (e.g. clear canvas)
        if (config.beforeDraw) {
          config.beforeDraw();
        }

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawX(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      width_px: number;
      color?: string;
    }
  ): Promise<void> {
    return new Promise((resolve) => {
      const scaledSize = this.getScaledWidth(config.width_px);

      const img = new Image(scaledSize, scaledSize);

      const svgXml = CROSS2_SVG.replaceAll(
        'currentColor',
        // default is to replace the white with red
        config.color ?? SVG_FALLBACK_CROSSHAIR_COLOR
      );

      // ? encodeURIComponent is necessary to safely handle special chars, e.g. in hex-style color codes
      img.src =
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgXml);

      img.onload = () => {
        const x = this.inchToPxX(config.value.xInches);
        const y = this.inchToPxY(config.value.zInches);
        const half = scaledSize / 2;

        // rescale the image to match target size
        ctx.drawImage(img, x - half, y - half, scaledSize, scaledSize);

        resolve();
      };
    });
  }

  drawTrainingBreak(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      isNew: boolean;
      size: number;
      index: number;
    }
  ) {
    const scaledSize = this.getScaledWidth(config.size);

    if (
      config.value.xInches < this.CONFIG.x.min_in ||
      config.value.xInches > this.CONFIG.x.max_in
    ) {
      return;
    }

    if (
      config.value.zInches < this.CONFIG.y.min_in ||
      config.value.zInches > this.CONFIG.y.max_in
    ) {
      return;
    }

    const dots = this.CONFIG.isDark
      ? DARK_TRAINING_DOT_COLORS
      : LIGHT_TRAINING_DOT_COLORS;

    const colors = config.isNew ? dots.new : dots.old;

    ctx.lineWidth = this.getScaledWidth(2);
    ctx.fillStyle = colors.background;
    ctx.strokeStyle = colors.border;

    ctx.beginPath();
    ctx.ellipse(
      this.inchToPxX(config.value.xInches),
      this.inchToPxY(config.value.zInches),
      scaledSize,
      scaledSize,
      0,
      0,
      RAD_FULL_ROTATION
    );

    ctx.fill();
    ctx.stroke();

    ctx.font = getScaledLabelFont(this.CONFIG.canvas.widthScale);
    ctx.fillStyle = colors.label;

    // inside the circle
    ctx.fillText(
      config.index.toString(),
      this.inchToPxX(config.value.xInches) -
        scaledSize / 2 +
        this.getScaledWidth(1),
      this.inchToPxY(config.value.zInches) + scaledSize / 2
    );
  }

  drawDot(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      // radius of dot in inches
      radiusIn: number;
      // e.g. hex, rgba
      fillColor?: string;
      borderColor?: string;
      borderWidth?: number;
    }
  ) {
    const radiusPx = this.inchToPxX(config.radiusIn) - this.inchToPxX(0);

    if (
      config.value.xInches < this.CONFIG.x.min_in ||
      config.value.xInches > this.CONFIG.x.max_in
    ) {
      return;
    }

    if (
      config.value.zInches < this.CONFIG.y.min_in ||
      config.value.zInches > this.CONFIG.y.max_in
    ) {
      return;
    }

    // fallback is success-9
    ctx.fillStyle = config.fillColor ?? '#30A46C';

    ctx.beginPath();
    ctx.ellipse(
      this.inchToPxX(config.value.xInches),
      this.inchToPxY(config.value.zInches),
      radiusPx,
      radiusPx,
      0,
      0,
      RAD_FULL_ROTATION
    );

    ctx.fill();

    if (config.borderColor && config.borderWidth) {
      ctx.setLineDash([]);
      ctx.strokeStyle = config.borderColor;
      ctx.lineWidth = config.borderWidth;
      ctx.stroke();
    }

    ctx.closePath();
  }

  drawCircle(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      // radius of circle in inches
      radiusIn: number;
      borderColor: string;
      borderWidth: number;
      lineStyle?: 'solid' | 'dotted';
    }
  ) {
    this.drawEllipse(ctx, {
      value: config.value,
      xRadiusIn: config.radiusIn,
      yRadiusIn: config.radiusIn,
      borderColor: config.borderColor,
      borderWidth: config.borderWidth,
      lineStyle: config.lineStyle,
    });
  }

  drawEllipse(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      xRadiusIn: number;
      yRadiusIn: number;
      borderColor: string;
      borderWidth: number;
      lineStyle?: 'solid' | 'dotted';
      rotationRad?: number;
      startRad?: number;
      endRad?: number;
    }
  ) {
    ctx.setLineDash(config.lineStyle === 'dotted' ? [8, 8] : []);

    ctx.beginPath();
    ctx.ellipse(
      this.inchToPxX(config.value.xInches), // center X
      this.inchToPxY(config.value.zInches), // center Y
      this.inchToPxX(config.xRadiusIn) - this.inchToPxX(0), // radius X
      this.inchToPxX(config.yRadiusIn) - this.inchToPxY(0), // radius Y
      config.rotationRad ?? 0, // rotation
      config.startRad ?? 0, // start angle radians
      config.endRad ?? RAD_FULL_ROTATION // end angle radians
    );

    ctx.strokeStyle = config.borderColor;
    ctx.lineWidth = config.borderWidth;
    ctx.stroke();

    // reset the line style
    ctx.setLineDash([]);
  }

  // label will be positioned near the value coordinates
  // showing the deltas of subtracting the target from the value
  drawLabel(
    ctx: CanvasRenderingContext2D,
    config: {
      value: ITrajektRefBreak;
      target: ITrajektRefBreak;
      // e.g. hex, rgba
      fontColor: string;
    }
  ) {
    const roundTo1 = (v: number) => Math.round(v * 10) / 10;

    // label the point
    const delta_x_in = roundTo1(config.value.xInches - config.target.xInches);
    const delta_z_in = roundTo1(config.value.zInches - config.target.zInches);

    const formatLabel = (v: number) =>
      `${v > 0 ? '+' : v < 0 ? '-' : ''}${Math.abs(v).toFixed(1)}`;

    ctx.font = getScaledLabelFont(
      this.CONFIG.canvas.widthScale,
      BASE_RULER_FONT_SIZE
    );
    ctx.fillStyle = config.fontColor;

    ctx.fillText(
      `${formatLabel(delta_x_in)}", ${formatLabel(delta_z_in)}"`,
      this.inchToPxX(config.value.xInches) + 16,
      this.inchToPxY(config.value.zInches) + 16
    );
  }

  drawLine(
    ctx: CanvasRenderingContext2D,
    config: {
      from: ITrajektRefBreak;
      to: ITrajektRefBreak;
      lineColor: string;
    }
  ) {
    // draw the line to the target under the dot
    ctx.strokeStyle = config.lineColor;
    ctx.lineWidth = this.getScaledWidth(1); // line thickness

    ctx.setLineDash([8, 4]);

    ctx.beginPath();
    ctx.moveTo(
      this.inchToPxX(config.from.xInches),
      this.inchToPxY(config.from.zInches)
    );
    ctx.lineTo(
      this.inchToPxX(config.to.xInches),
      this.inchToPxY(config.to.zInches)
    );
    ctx.closePath();
    ctx.stroke();

    // reset line dash style for subsequent lines in this context
    ctx.setLineDash([]);
  }

  static formatDeltaIn = (deltaIn: number) => {
    const rounded = Math.round(deltaIn * 10) / 10;
    const sign = rounded > 0 ? '+' : rounded < 0 ? '-' : '';
    return `${sign}${Math.abs(rounded).toFixed(1)}`;
  };
}
